import React, { useEffect, useState } from 'react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import {
  TableContainer,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Table,
  Paper,
  TextField,
  CircularProgress,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Button,
  Stack
} from '@mui/material';
import { Search, ArrowUp, ArrowDown, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const Scope3DataSubmission = () => {
  const [companies, setCompanies] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // State for filters and sorting
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Add these states
  const [sortField, setSortField] = useState('company_name');
  const [sortDirection, setSortDirection] = useState('asc');


  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchData(newPage, rowsPerPage, searchTerm, filterType, sortBy, sortOrder);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData(0, newRowsPerPage, searchTerm, filterType, sortBy, sortOrder);
  };

  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
    setPage(0);
    fetchData(0, rowsPerPage, searchTerm, event.target.value, sortBy, sortOrder);
  };

  const handleSortFieldChange = (event) => {
    const newSortField = event.target.value;
    setSortField(newSortField);
    fetchData(0, rowsPerPage, searchTerm, filterType, newSortField, sortDirection);
    setPage(0);
  };

  const handleSortDirectionChange = (event) => {
    const newSortDirection = event.target.value;
    setSortDirection(newSortDirection);
    fetchData(0, rowsPerPage, searchTerm, filterType, sortField, newSortDirection);
    setPage(0);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData(0, rowsPerPage, searchTerm, filterType, sortBy, sortOrder);
      setPage(0);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchData = async (currentPage, limit, search, filter, sort, order) => {
    setLoading(true);
    try {
      let url = `/admin/scope3submissionData?page=${currentPage}&limit=${limit}&filterType=${filter}&sortBy=${sort}&sortOrder=${order}`;

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await api.get(url);
      setCompanies(response.data.data);
      setTotalCompanies(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, rowsPerPage, searchTerm, filterType, sortBy, sortOrder);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not sent for review';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <>
      <PageHeader title="Scope3 Data Submission">
        <div className="flex justify-between items-center gap-5 mt-5 flex-wrap">
          <div className="flex items-center gap-4 flex-1">
            <TextField
              size="small"
              placeholder="Search company..."
              value={searchTerm}
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="min-w-[300px]"
            />


            <Stack direction="row" spacing={2} alignItems="center">
              {/* <FormControl size="small" className="min-w-[200px]">
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterType}
                  label="Filter"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  <MenuItem value="with_files">With Files</MenuItem>
                  <MenuItem value="review_done">Review Done</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="available_for_review">Available for Review</MenuItem>
                  <MenuItem value="unavailable_for_review">Unavailable for Review</MenuItem>
                </Select>
              </FormControl> */}

              {/* // Add inside your sort dropdown: "Sort By" */}
              <FormControl size="small" className="min-w-[150px]">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortField}
                  label="Sort By"
                  onChange={handleSortFieldChange}
                >
                  <MenuItem value="company_name">Company Name</MenuItem>
                  <MenuItem value="created_at">Joined On (Created Date)</MenuItem>
                </Select>
              </FormControl>


              <FormControl size="small" className="min-w-[150px]">
                <InputLabel>Sort Order</InputLabel>
                <Select
                  value={sortDirection}
                  label="Sort Order"
                  onChange={handleSortDirectionChange}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </div>
        </div>

        <TableContainer
          component={Paper}
          sx={{ marginTop: 3 }}
          className="shadow-md rounded-lg overflow-hidden"
        >
          {loading ? (
            <div className="flex justify-center items-center p-16">
              <CircularProgress />
            </div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Sector</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Joined On</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies?.length > 0 ? (
                  companies.map((company) => (
                    <TableRow
                      key={company.id}
                      hover
                      className="cursor-pointer"
                      onClick={() => window.open(`/admin/scope3_data_submission/${company.company_id}`, "_blank")}
                      title="Click to open company scope 3 documents in new window"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {company.company_logo && (
                            <img
                              src={company.company_logo}
                              alt={`${company.company_full_name} logo`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          )}
                          <span className="font-medium">{company.company_full_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{company.country_name}</TableCell>
                      <TableCell>{company.sector_name}</TableCell>
                      <TableCell>{company.industry_name}</TableCell>
                      <TableCell>{formatDate(company.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <div className="py-8 text-gray-500">No data found</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    colSpan={4}
                    count={totalCompanies}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) =>
                      `Page ${page + 1} of ${Math.ceil(count / rowsPerPage) || 1} | ${from}-${to} of ${count}`
                    }
                  />
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </TableContainer>
      </PageHeader>
    </>
  );
};

export default Scope3DataSubmission;